<script>
  import { onMount } from 'svelte';
  import Footer from '../components/Footer.svelte';
  import Header from '../components/Header.svelte';
  import PricingCard from '../components/PricingCard.svelte';
  import { pricingSection } from '../sections/participate.js';
  import { resolveProductId } from '../sections/registration.js';
  import { precalculateOrder } from '../services/gigmaApi.js';

  import { goBack } from '../utils/router.js';

  // Динамические цены тарифов из Gigma. См. комментарий в
  // ParticipateSection.svelte — тот же гибридный подход (хардкод как
  // fallback, актуальная цена с бэка при успехе).
  let dynamicPrices = {};

  function formatPrice(value) {
    if (!Number.isFinite(value)) return '';
    return new Intl.NumberFormat('ru-RU').format(value) + ' ₽';
  }

  onMount(() => {
    for (const plan of pricingSection.plans) {
      const id = resolveProductId(plan.slug);
      precalculateOrder(id, null, 1)
        .then((data) => {
          const price = Number(data?.final_price);
          if (Number.isFinite(price)) {
            dynamicPrices = { ...dynamicPrices, [plan.slug]: formatPrice(price) };
          }
        })
        .catch(() => {});
    }
  });
</script>

<div class="tariff-page">
  <Header />

  <div class="tariff-section">
    <div class="tariff-frame">
      <nav class="tariff-topbar" aria-label="Навигация">
        <button class="back-btn" on:click={goBack} aria-label="Назад">
          <svg class="back-icon" viewBox="0 0 20 20" aria-hidden="true">
            <path d="M13 4L7 10l6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
          </svg>
          <span>Назад</span>
        </button>
      </nav>

      <div class="tariff-content">
        <div class="tariff-header">
          <h1 class="tariff-title">Выбор тарифа</h1>
          <p class="tariff-description">Выберите подходящий пакет — получить максимум пользы от форума и деловых контактов.</p>
        </div>

        <div class="pricing-grid">
          {#each pricingSection.plans as plan}
            <PricingCard
              title={plan.title}
              subtitle={plan.subtitle}
              price={dynamicPrices[plan.slug] ?? plan.price}
              features={plan.features}
              popular={plan.popular || false}
              action={{ label: pricingSection.actionLabel, href: `/registration?product=${plan.slug}` }}
            />
          {/each}
        </div>
      </div>
    </div>
  </div>

  <Footer />
</div>

<style>
  .tariff-page {
    position: relative;
    min-height: 100vh;
    background: #000000;
    color: #ffffff;
    font-family: Roboto, Arial, Helvetica, sans-serif;
    font-variation-settings: 'wdth' 100;
  }

  .tariff-section {
    display: flex;
    justify-content: center;
    width: 100%;
    padding: 100px 0 80px;
  }

  .tariff-frame {
    display: flex;
    width: min(100%, 1440px);
    flex-direction: column;
    gap: 10px;
    padding: 0 64px;
    box-sizing: border-box;
  }

  .tariff-topbar {
    display: flex;
    align-items: center;
    min-height: 40px;
  }

  .back-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 0;
    background: none;
    border: none;
    color: #ffffff;
    font-family: Manrope, Arial, Helvetica, sans-serif;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.2;
    cursor: pointer;
    border-radius: 8px;
  }

  .back-btn:hover,
  .back-btn:focus-visible {
    color: #e7b962;
  }

  .back-icon {
    width: 20px;
    height: 20px;
    flex: 0 0 auto;
  }

  .tariff-content {
    display: flex;
    flex-direction: column;
    gap: 0;
    padding: 0 12px;
  }

  .tariff-header {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .tariff-title {
    margin: 0;
    color: #ffffff;
    font-family: Roboto, Arial, Helvetica, sans-serif;
    font-size: 56px;
    font-weight: 700;
    line-height: 1;
    font-variation-settings: 'wdth' 100;
  }

  .tariff-description {
    margin: 0;
    padding: 32px 30px 48px 0;
    color: rgba(255, 255, 255, 0.5);
    font-family: Roboto, Arial, Helvetica, sans-serif;
    font-size: 24px;
    font-weight: 400;
    line-height: 1.5;
    font-variation-settings: 'wdth' 100;
  }

  .pricing-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    align-items: stretch;
    gap: 24px;
    padding: 24px 0;
  }

  /* На 1280–1439 4 в ряд не помещаются — переходим на 2×2 (см. Codex P2). */
  @media (max-width: 1439px) {
    .pricing-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 32px;
    }
  }

  @media (max-width: 1279px) {
    .tariff-frame {
      padding: 0 32px;
    }

    .tariff-section {
      padding: 100px 0 80px;
    }

    .tariff-title {
      font-size: 40px;
    }

    .tariff-description {
      font-size: 18px;
      padding: 24px 0 32px;
      line-height: 28px;
    }

    /* .pricing-grid → 2×2 уже в @media (max-width: 1439px) выше. */
  }

  @media (max-width: 1599px) and (min-width: 1280px) {
    .tariff-title {
      font-size: 48px;
    }

    .tariff-description {
      font-size: 20px;
      padding: 24px 30px 40px 0;
      line-height: 30px;
    }
  }

  @media (max-width: 767px) {
    .tariff-section {
      padding: 100px 0 40px;
    }

    .tariff-frame {
      padding: 0 24px;
    }

    .tariff-content {
      padding: 0;
    }

    .tariff-title {
      font-size: 32px;
      line-height: 1;
    }

    .tariff-description {
      padding: 24px 0;
      font-size: 18px;
      color: rgba(255, 255, 255, 0.7);
    }

    .pricing-grid {
      grid-template-columns: 1fr;
      gap: 48px;
    }
  }
</style>
